import Card from "@/components/common/Card";
import { prepareDefinition } from "@/components/segments/conditions/segmentFormUtils";
import {
  conditionFieldLabel,
  conditionValueText,
  operatorLabel,
} from "@/components/segments/conditions/conditionDisplay";

const KIND_BITS = [
  { label: "Dynamic", bit: 1 },
  { label: "Overlay", bit: 2 },
  { label: "Banner", bit: 4 },
  { label: "Static", bit: 8 },
];

const GROUP_LABELS = {
  all: "Must match all",
  any: "Match any",
  none: "Exclude when",
};

export default function Step3Review({
  form,
  updateField,
  onNumberChange,
  fieldOptionConfig,
}) {
  // Show exactly what will be saved: normalized, with unfilled conditions dropped.
  const definition = prepareDefinition(form.definition);
  return (
    <Card className="builder-form flex flex-col gap-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="form-group-modal">
          <label htmlFor="segment-cpm">CPM</label>
          <input
            id="segment-cpm"
            type="number"
            min="0"
            value={form.cpm ?? ""}
            onChange={(event) => onNumberChange("cpm", event.target.value)}
            placeholder="e.g. 100"
          />
        </div>
        <div className="form-group-modal">
          <label>Type</label>
          <div className="kind-picker">
            {KIND_BITS.map(({ label, bit }) => (
              <label key={bit} className="kind-checkbox">
                <input
                  type="checkbox"
                  checked={((form.kind ?? 15) & bit) !== 0}
                  onChange={() => updateField("kind", (form.kind ?? 15) ^ bit)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="builder-divider" aria-hidden />

      <div>
        <h3 className="text-base font-semibold text-(--text-primary)">
          Review targeting
        </h3>
        <p className="mt-1 text-sm text-(--text-secondary)">
          Double-check the rules before saving. Go back to adjust any step.
        </p>
        <div className="mt-4 flex flex-col gap-4">
          {["all", "any", "none"].map((group) => {
            const conditions = definition[group] ?? [];
            if (conditions.length === 0) return null;
            return (
              <div
                key={group}
                className="rounded-(--radius-md) border border-(--border-subtle) p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-(--text-primary)">
                    {GROUP_LABELS[group]}
                  </span>
                  <span className="text-xs text-(--text-muted)">
                    {conditions.length} condition
                    {conditions.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-(--text-secondary)">
                  {conditions.map((condition, index) => (
                    <li
                      key={`${condition.field}-${index}`}
                      className={condition.disabled ? "line-through" : ""}
                    >
                      <span className="font-medium text-(--text-primary)">
                        {conditionFieldLabel(condition)}
                      </span>{" "}
                      <span className="text-(--text-muted)">
                        {operatorLabel(condition.op)}
                      </span>{" "}
                      {conditionValueText(condition, fieldOptionConfig)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {["all", "any", "none"].every(
            (group) => (definition[group] ?? []).length === 0,
          ) && (
            <p className="text-sm text-(--text-muted)">
              No conditions added yet.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
